import { useState } from "react";
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useDemo } from "@/components/ui/demo-provider";

const LINKS = [
  { label: "Platform", to: "/#platform" },
  { label: "Payroll", to: "/#payroll" },
  { label: "Analysis", to: "/#analysis" },
  { label: "Pack", to: "/#month-end" },
  { label: "Workforce", to: "/#workforce" },
  { label: "Architecture", to: "/#architecture" },
  { label: "Spectrum", to: "/#spectrum" },
  { label: "Security", to: "/#security" },
];

export default function Navbar() {
  const { open } = useDemo();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 24));

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[color:var(--bg-nav-scrolled)] backdrop-blur-xl" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href="/" className="group flex items-center" data-testid="nav-logo" aria-label="RORAQ home">
          <img
            src="/brand/lockup-white.png"
            alt="RORAQ"
            className="h-9 w-auto sm:h-10"
            draggable={false}
          />
        </a>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="rounded-full px-4 py-2 text-[13px] font-medium tracking-wide text-slate-400 transition-colors duration-300 hover:bg-white/[0.05] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-primary)]"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-2.5 lg:flex">
          <a
            href="https://app.roraq.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-login-link"
            className="group flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-medium text-slate-300 transition-colors duration-300 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-primary)]"
          >
            Log in
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button type="button" onClick={open} data-testid="nav-request-demo-button" className="btn-primary !px-5 !py-2.5 !text-[13px]">
            Request a demo
          </button>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <button
            type="button"
            className="rounded-lg border border-[color:var(--border-subtle)] p-2 text-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal/50"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="nav-mobile-menu-button"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-[color:var(--border-subtle)] bg-[color:var(--bg-nav-scrolled)] backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.label}
                  href={l.to}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-lg px-3 py-3 text-sm font-medium text-slate-300 hover:bg-white/[0.05] hover:text-white"
                >
                  {l.label}
                </a>
              ))}
              <div className="mt-3 flex items-center gap-3 border-t border-[color:var(--border-subtle)] pt-4">
                <a
                  href="https://app.roraq.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost flex-1 !py-2.5 text-center !text-[13px]"
                >
                  Log in
                </a>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    open();
                  }}
                  className="btn-primary flex-1 !py-2.5 !text-[13px]"
                  data-testid="nav-mobile-request-demo-button"
                >
                  Request a demo
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
